import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, MapPin, Check, UserPlus } from "lucide-react";
import { format, addHours } from "date-fns";
import { EventSummary } from "@/pages/Index";
import { StatusPill } from "@/components/brand/StatusPill";
import { Figure, Eyebrow } from "@/components/brand/SectionHeader";

interface EventCardProps {
  event: EventSummary;
}

const EventCard = ({ event }: EventCardProps) => {
  const startDate = new Date(event.startTime);
  const endDate = addHours(startDate, event.eventLength);

  // Check if dates are valid before formatting
  const isStartDateValid = !isNaN(startDate.getTime());
  const isEndDateValid = !isNaN(endDate.getTime());

  return (
    <Link
      to={`/event/${event.eventId}`}
      className="group block px-5 py-4 transition-colors hover:bg-primary/[0.06]"
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-xl text-foreground">
              {event.companyName}
            </h3>
            {event.isUserRegistered && (
              <StatusPill tone="success" icon={Check}>
                Registered
              </StatusPill>
            )}
            {event.userOnWaitlist && !event.isUserRegistered && (
              <StatusPill tone="muted" icon={UserPlus}>
                Waitlisted
              </StatusPill>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <CalendarDays className="h-4 w-4" />
              <span>
                {isStartDateValid
                  ? format(startDate, "MMM d, yyyy")
                  : "Date unavailable"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>
                {isStartDateValid && isEndDateValid
                  ? `${format(startDate, "h:mm a")} - ${format(
                      endDate,
                      "h:mm a",
                    )}`
                  : "Time unavailable"}
              </span>
            </div>
            {/* A pin with nothing after it reads as a failed lookup rather
                than an absent field. */}
            {event.city && (
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                <span>{event.city}</span>
              </div>
            )}
          </div>
          {event.isUserRegistered && event.userPositions.length > 0 && (
            <div className="mt-2">
              <div className="flex flex-wrap gap-2">
                {event.userPositions.map((position) => (
                  <span
                    key={position}
                    className="inline-block rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                  >
                    {position}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-5">
          <div className="text-left sm:text-right">
            <Figure className="block text-xl leading-none text-foreground">
              {event.filledPositions}/{event.totalPositions}
            </Figure>
            <Eyebrow className="mt-1">Filled</Eyebrow>
          </div>
          {/* The whole row is the link, so this is an affordance rather than
              a control: pointer-events-none lets the click through to it. */}
          <Button
            variant="outline"
            className="sheen pointer-events-none shrink-0 border-primary/50 transition-colors group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground"
          >
            View Details
          </Button>
        </div>
      </div>
    </Link>
  );
};

export default EventCard;
